import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Reveal } from "./Reveal";
import { COMPANY, SERVICE_OPTIONS } from "../lib/site";
import {
    MapPin,
    Phone,
    Mail,
    Globe,
    Facebook,
    Loader2,
    Send,
    CheckCircle2,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = {
    full_name: "",
    email: "",
    phone: "",
    service: "",
    message: "",
    consent: false,
};

const CONTACT_ITEMS = [
    {
        icon: MapPin,
        label: "Address",
        value: COMPANY.address,
        href: null,
        testid: "contact-address",
    },
    {
        icon: Phone,
        label: "Phone",
        value: COMPANY.phoneDisplay,
        href: COMPANY.phoneHref,
        testid: "contact-phone",
    },
    {
        icon: Mail,
        label: "Email",
        value: COMPANY.email,
        href: COMPANY.emailHref,
        testid: "contact-email",
    },
    {
        icon: Globe,
        label: "Website",
        value: COMPANY.websiteDisplay,
        href: COMPANY.website,
        external: true,
        testid: "contact-website",
    },
    {
        icon: Facebook,
        label: "Facebook",
        value: "Lynxtech IT Solutions",
        href: COMPANY.facebook,
        external: true,
        testid: "contact-facebook",
    },
];

export default function ContactForm() {
    const [form, setForm] = useState(EMPTY);
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [done, setDone] = useState(false);

    const setField = (name, value) => {
        setForm((f) => ({ ...f, [name]: value }));
        setErrors((e) => ({ ...e, [name]: undefined }));
    };

    const validate = () => {
        const e = {};
        if (!form.full_name.trim() || form.full_name.trim().length < 2)
            e.full_name = "Please enter your full name.";
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim()))
            e.email = "Please enter a valid email address.";
        if (!form.service) e.service = "Please select a service.";
        if (!form.message.trim() || form.message.trim().length < 5)
            e.message = "Please tell us a little about your project.";
        if (!form.consent)
            e.consent = "Please provide consent so we can respond to you.";
        setErrors(e);
        return Object.keys(e).length === 0;
    };

    const onSubmit = async (ev) => {
        ev.preventDefault();
        if (!validate()) {
            toast.error("Please check the highlighted fields.");
            return;
        }
        setLoading(true);
        try {
            await axios.post(`${API}/contact/inquiry`, {
                full_name: form.full_name.trim(),
                email: form.email.trim(),
                phone: form.phone.trim() || null,
                service: form.service,
                message: form.message.trim(),
                consent: form.consent,
            });
            setDone(true);
            setForm(EMPTY);
            toast.success("Inquiry sent! We'll get back to you soon.");
        } catch (err) {
            toast.error(
                "Something went wrong sending your inquiry. Please try again or email us directly.",
            );
        } finally {
            setLoading(false);
        }
    };

    const inputBase =
        "w-full rounded-xl border bg-white px-4 py-3 text-sm text-navy outline-none transition-colors placeholder:text-slate-400 focus:border-brand focus:ring-2 focus:ring-brand/20";

    return (
        <section
            id="contact"
            data-testid="contact-section"
            className="bg-slate-50 py-20 lg:py-32"
        >
            <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-12">
                <Reveal className="mb-14 max-w-2xl">
                    <span className="font-mono text-xs font-semibold uppercase tracking-[0.2em] text-brand">
                        Let's Connect
                    </span>
                    <h2 className="mt-4 font-display text-3xl font-bold tracking-tight text-navy sm:text-4xl">
                        Have a project in mind?
                    </h2>
                    <p className="mt-4 text-base leading-relaxed text-slate-600">
                        Tell us what you need and we can explore the right next step
                        together.
                    </p>
                </Reveal>

                <div className="grid grid-cols-1 gap-8 lg:grid-cols-[0.85fr_1.15fr]">
                    {/* Contact info panel */}
                    <Reveal>
                        <div className="grain relative h-full overflow-hidden rounded-3xl bg-navy p-8 text-white">
                            <div
                                className="pointer-events-none absolute -right-16 -top-16 h-56 w-56 rounded-full opacity-40 blur-3xl"
                                style={{
                                    background:
                                        "radial-gradient(circle, rgba(56,189,248,0.5), transparent 70%)",
                                }}
                                aria-hidden="true"
                            />
                            <h3 className="relative font-display text-xl font-semibold">
                                Lynxtech IT Solutions
                            </h3>
                            <p className="relative mt-2 text-sm text-slate-400">
                                Technology platforms, software, hardware & networks,
                                CCTV, and Starlink installation.
                            </p>

                            <ul className="relative mt-8 space-y-5">
                                {CONTACT_ITEMS.map((c) => (
                                    <li
                                        key={c.label}
                                        className="flex items-start gap-3"
                                    >
                                        <span className="mt-0.5 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-white/10 text-sky-300">
                                            <c.icon className="h-4 w-4" />
                                        </span>
                                        <div>
                                            <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-slate-500">
                                                {c.label}
                                            </div>
                                            {c.href ? (
                                                <a
                                                    href={c.href}
                                                    data-testid={c.testid}
                                                    {...(c.external
                                                        ? {
                                                              target: "_blank",
                                                              rel: "noopener noreferrer",
                                                          }
                                                        : {})}
                                                    className="text-sm font-medium text-slate-100 transition-colors hover:text-sky-300"
                                                >
                                                    {c.value}
                                                </a>
                                            ) : (
                                                <span
                                                    data-testid={c.testid}
                                                    className="text-sm font-medium text-slate-100"
                                                >
                                                    {c.value}
                                                </span>
                                            )}
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </Reveal>

                    {/* Form */}
                    <Reveal delay={0.1}>
                        <div className="rounded-3xl border border-slate-200 bg-white p-7 shadow-sm sm:p-9">
                            {done ? (
                                <motion.div
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    data-testid="contact-success"
                                    className="flex h-full flex-col items-center justify-center py-12 text-center"
                                >
                                    <CheckCircle2 className="h-14 w-14 text-brand" />
                                    <h3 className="mt-5 font-display text-2xl font-bold text-navy">
                                        Thank you!
                                    </h3>
                                    <p className="mt-2 max-w-md text-sm text-slate-600">
                                        Your inquiry has been received and sent to our
                                        team. We'll get back to you at the email you
                                        provided as soon as possible.
                                    </p>
                                    <button
                                        type="button"
                                        onClick={() => setDone(false)}
                                        data-testid="contact-send-another"
                                        className="mt-6 rounded-full border border-slate-200 px-6 py-2.5 text-sm font-semibold text-navy transition-colors hover:bg-slate-50"
                                    >
                                        Send another inquiry
                                    </button>
                                </motion.div>
                            ) : (
                                <form
                                    onSubmit={onSubmit}
                                    data-testid="contact-form"
                                    noValidate
                                    className="space-y-5"
                                >
                                    <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                                        <div>
                                            <label
                                                htmlFor="full_name"
                                                className="mb-1.5 block text-sm font-medium text-navy"
                                            >
                                                Full Name{" "}
                                                <span className="text-brand">*</span>
                                            </label>
                                            <input
                                                id="full_name"
                                                type="text"
                                                value={form.full_name}
                                                onChange={(e) =>
                                                    setField(
                                                        "full_name",
                                                        e.target.value,
                                                    )
                                                }
                                                data-testid="contact-input-name"
                                                aria-invalid={!!errors.full_name}
                                                placeholder="Juan Dela Cruz"
                                                className={`${inputBase} ${
                                                    errors.full_name
                                                        ? "border-red-400"
                                                        : "border-slate-200"
                                                }`}
                                            />
                                            {errors.full_name && (
                                                <p
                                                    data-testid="error-name"
                                                    className="mt-1 text-xs text-red-500"
                                                >
                                                    {errors.full_name}
                                                </p>
                                            )}
                                        </div>

                                        <div>
                                            <label
                                                htmlFor="email"
                                                className="mb-1.5 block text-sm font-medium text-navy"
                                            >
                                                Email Address{" "}
                                                <span className="text-brand">*</span>
                                            </label>
                                            <input
                                                id="email"
                                                type="email"
                                                value={form.email}
                                                onChange={(e) =>
                                                    setField(
                                                        "email",
                                                        e.target.value,
                                                    )
                                                }
                                                data-testid="contact-input-email"
                                                aria-invalid={!!errors.email}
                                                placeholder="you@example.com"
                                                className={`${inputBase} ${
                                                    errors.email
                                                        ? "border-red-400"
                                                        : "border-slate-200"
                                                }`}
                                            />
                                            {errors.email && (
                                                <p
                                                    data-testid="error-email"
                                                    className="mt-1 text-xs text-red-500"
                                                >
                                                    {errors.email}
                                                </p>
                                            )}
                                        </div>

                                        <div>
                                            <label
                                                htmlFor="phone"
                                                className="mb-1.5 block text-sm font-medium text-navy"
                                            >
                                                Contact Number{" "}
                                                <span className="text-slate-400">
                                                    (optional)
                                                </span>
                                            </label>
                                            <input
                                                id="phone"
                                                type="tel"
                                                value={form.phone}
                                                onChange={(e) =>
                                                    setField(
                                                        "phone",
                                                        e.target.value,
                                                    )
                                                }
                                                data-testid="contact-input-phone"
                                                placeholder="+63 9XX XXX XXXX"
                                                className={`${inputBase} border-slate-200`}
                                            />
                                        </div>

                                        <div>
                                            <label
                                                htmlFor="service"
                                                className="mb-1.5 block text-sm font-medium text-navy"
                                            >
                                                Service Interested In{" "}
                                                <span className="text-brand">*</span>
                                            </label>
                                            <select
                                                id="service"
                                                value={form.service}
                                                onChange={(e) =>
                                                    setField(
                                                        "service",
                                                        e.target.value,
                                                    )
                                                }
                                                data-testid="contact-select-service"
                                                aria-invalid={!!errors.service}
                                                className={`${inputBase} ${
                                                    errors.service
                                                        ? "border-red-400"
                                                        : "border-slate-200"
                                                } ${form.service ? "text-navy" : "text-slate-400"}`}
                                            >
                                                <option value="" disabled>
                                                    Select a service
                                                </option>
                                                {SERVICE_OPTIONS.map((o) => (
                                                    <option
                                                        key={o.value}
                                                        value={o.value}
                                                    >
                                                        {o.label}
                                                    </option>
                                                ))}
                                            </select>
                                            {errors.service && (
                                                <p
                                                    data-testid="error-service"
                                                    className="mt-1 text-xs text-red-500"
                                                >
                                                    {errors.service}
                                                </p>
                                            )}
                                        </div>
                                    </div>

                                    <div>
                                        <label
                                            htmlFor="message"
                                            className="mb-1.5 block text-sm font-medium text-navy"
                                        >
                                            Project Details / Message{" "}
                                            <span className="text-brand">*</span>
                                        </label>
                                        <textarea
                                            id="message"
                                            rows={5}
                                            value={form.message}
                                            onChange={(e) =>
                                                setField(
                                                    "message",
                                                    e.target.value,
                                                )
                                            }
                                            data-testid="contact-input-message"
                                            aria-invalid={!!errors.message}
                                            placeholder="Tell us about your project, timeline, and requirements…"
                                            className={`${inputBase} resize-y ${
                                                errors.message
                                                    ? "border-red-400"
                                                    : "border-slate-200"
                                            }`}
                                        />
                                        {errors.message && (
                                            <p
                                                data-testid="error-message"
                                                className="mt-1 text-xs text-red-500"
                                            >
                                                {errors.message}
                                            </p>
                                        )}
                                    </div>

                                    <div>
                                        <label className="flex items-start gap-3">
                                            <input
                                                type="checkbox"
                                                checked={form.consent}
                                                onChange={(e) =>
                                                    setField(
                                                        "consent",
                                                        e.target.checked,
                                                    )
                                                }
                                                data-testid="contact-checkbox-consent"
                                                className="mt-0.5 h-4 w-4 shrink-0 rounded border-slate-300 text-brand focus:ring-brand"
                                            />
                                            <span className="text-xs leading-relaxed text-slate-600">
                                                I consent to Lynxtech IT Solutions
                                                processing the details I've provided in
                                                order to respond to my inquiry.
                                            </span>
                                        </label>
                                        {errors.consent && (
                                            <p
                                                data-testid="error-consent"
                                                className="mt-1 text-xs text-red-500"
                                            >
                                                {errors.consent}
                                            </p>
                                        )}
                                    </div>

                                    <button
                                        type="submit"
                                        disabled={loading}
                                        data-testid="contact-submit-button"
                                        className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-brand px-8 py-3.5 text-sm font-semibold text-white shadow-[0_14px_30px_rgba(2,132,199,0.35)] transition-all duration-200 hover:-translate-y-0.5 hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-70 sm:w-auto"
                                    >
                                        {loading ? (
                                            <>
                                                <Loader2 className="h-4 w-4 animate-spin" />
                                                Sending…
                                            </>
                                        ) : (
                                            <>
                                                <Send className="h-4 w-4" />
                                                Send Inquiry
                                            </>
                                        )}
                                    </button>
                                </form>
                            )}
                        </div>
                    </Reveal>
                </div>
            </div>
        </section>
    );
}
